/**
 * A mission's request for a human gesture on a screen as the card reads it (`get_mission_screen_request`,
 * the single SQL reader; PLAN-ATELIER-OTTO § 17): what to do, why, where. The server decides it and
 * re-checks the answer; the card parses what it is given and says it.
 */
import { z } from 'zod';

import { heldByAnotherNotice } from './workshopControl';

export const missionScreenRequestSchema = z.object({
  /** The `escalate_human` action carrying the request — what an answer closes. */
  action_id: z.string().uuid(),
  asked_at: z.string(),
  /** Why Otto asks for the gesture, in his words. */
  question: z.string(),
  screen: z.literal('forms_browser'),
  gesture: z.string(),
  site: z.string().regex(/^https:\/\//).nullable(),
});
export type MissionScreenRequest = z.infer<typeof missionScreenRequestSchema>;

/** How the human answers — the server's `SCREEN_ANSWER_OUTCOMES`. */
export type ScreenAnswerOutcome = 'done' | 'cannot';

/** Same floor as the server for « Je ne peux pas »: the word is what Otto reads to decide. */
export const SCREEN_NOTE_MIN_CHARS = 8;

/** The reader's answer, or null for any other wait (and for an answer the card cannot read). Pure. */
export function parseScreenRequest(data: unknown): MissionScreenRequest | null {
  if (data === null || data === undefined) return null;
  const parsed = missionScreenRequestSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

/** Why the human may not act in the screen now, in words, or null when they may. Pure. */
export function screenActBlocker(
  read: { can_control: boolean; screen_controls: boolean; held_by_another?: boolean } | null,
): string | null {
  if (!read) return null;
  const held = heldByAnotherNotice(read);
  if (held) return held;
  if (!read.can_control) return 'Seule une personne nommée pour trancher les livrables peut agir dans cet écran.';
  if (!read.screen_controls) return "Un atelier formulaire tient le navigateur en ce moment : attends sa fin pour faire le geste.";
  return null;
}
